import {
  blue,
  CircularProgress,
  CircularProgressProps,
  cyan,
} from "@faro-lotv/flat-ui";
import { Box } from "@mui/system";
import { PropsWithChildren, useEffect, useState, type JSX } from "react";

type LoadingOverlayProps = Pick<CircularProgressProps, "size"> & {
  /** Whether the overlay is visible */
  isLoading: boolean;

  /** Whether to use the dark styles */
  dark?: boolean;
};

/**
 * @returns A container that can hide its content behind a loading spinner.
 */
export function LoadingOverlay({
  isLoading,
  dark,
  children,
  ...circularProgressProps
}: PropsWithChildren<LoadingOverlayProps>): JSX.Element {
  const [container, setContainer] = useState<HTMLDivElement>();

  // This effect manually assigns the "inert" attribute to disable interactions,
  // which is widely available in HTML since 2023, but only supported starting in react 19 in JSX.
  useEffect(() => {
    if (!container || !isLoading) return;
    container.setAttribute("inert", "");
    return () => container.removeAttribute("inert");
  }, [container, isLoading]);

  return (
    <Box ref={setContainer} component="div" sx={{ position: "relative" }}>
      <Box
        component="div"
        sx={{
          opacity: isLoading ? 0.5 : 1,
        }}
      >
        {children}
      </Box>
      {isLoading && (
        <CircularProgress
          sx={{
            color: dark ? cyan[500] : blue[500],
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          {...circularProgressProps}
        />
      )}
    </Box>
  );
}
